import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Check,
  X,
  AlertTriangle,
  ExternalLink,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Shield,
  Zap,
  Calendar,
  DollarSign,
  Mail,
  MessageSquare,
  Database,
  Bot,
  Activity,
  UserPlus,
  Eye,
  EyeOff,
  Users,
  Search,
  MoreVertical,
  Clock,
  CheckCircle,
  XCircle,
  Crown,
  ArrowRightLeft,
  Upload,
} from "lucide-react";
import AgentImportWizard from "../components/import/AgentImportWizard";
import AiAccessKeysPanel from "../components/settings/AiAccessKeysPanel";
import googleService from "../services/googleService";
import settingsService from "../services/settingsService";
import organizationService from "../services/organizationService";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";

const ProfileSettings = () => {
  const { user } = useAuth();
  const [showEdit, setShowEdit] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Initialize profile data from user context
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      });
    }
  }, [user]);

  const handleProfileChange = (e) => {
    setProfileData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!profileData.firstName.trim() || !profileData.lastName.trim()) {
      setMessage("First name and last name are required");
      setMessageType("error");
      return;
    }

    if (!profileData.email.trim()) {
      setMessage("Email is required");
      setMessageType("error");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(profileData.email)) {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.put(`/auth/users/${user.id}`, {
        firstName: profileData.firstName.trim(),
        lastName: profileData.lastName.trim(),
        email: profileData.email.trim(),
      });

      setMessage("Profile updated successfully!");
      setMessageType("success");
      setShowEdit(false);

      // TODO: Update the user context with new data
      // This would require adding an updateUser method to AuthContext
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to update profile. Please try again.";
      setMessage(message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="profile-settings" className="space-y-4">
      {message && (
        <div
          className={`p-3 rounded-lg text-sm flex items-center ${
            messageType === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {messageType === "success" ? (
            <Check className="w-4 h-4 mr-2" />
          ) : (
            <AlertTriangle className="w-4 h-4 mr-2" />
          )}
          {message}
        </div>
      )}

      {!showEdit ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Name</h3>
              <p className="text-sm text-gray-600">
                {user?.fullName ||
                  `${profileData.firstName} ${profileData.lastName}`}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <div>
              <h3 className="text-sm font-medium text-gray-900">Email</h3>
              <p className="text-sm text-gray-600">{profileData.email}</p>
            </div>
          </div>
          <div className="pt-3">
            <button
              onClick={() => setShowEdit(true)}
              className="btn-brand-primary btn-brand-sm inline-flex items-center"
            >
              <Edit className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Edit Profile</span>
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              First Name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              value={profileData.firstName}
              onChange={handleProfileChange}
            />
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Last Name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              value={profileData.lastName}
              onChange={handleProfileChange}
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              value={profileData.email}
              onChange={handleProfileChange}
            />
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="btn-brand-primary disabled:opacity-50 inline-flex items-center justify-center"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowEdit(false);
                setMessage("");
                // Reset form data
                if (user) {
                  setProfileData({
                    firstName: user.firstName || "",
                    lastName: user.lastName || "",
                    email: user.email || "",
                  });
                }
              }}
              className="btn-brand-secondary-outline inline-flex items-center justify-center"
            >
              <span>Cancel</span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const PasswordSettings = () => {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const handlePasswordChange = (e) => {
    setPasswordData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage("New passwords do not match");
      setMessageType("error");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage("Password must be at least 8 characters long");
      setMessageType("error");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const response = await api.post("/auth/change-password", {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setMessage("Password changed successfully!");
      setMessageType("success");
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowChangePassword(false);
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to change password. Please try again.";
      setMessage(message);
      setMessageType("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="password-settings" className="space-y-4">
      {message && (
        <div
          className={`p-3 rounded-lg text-sm flex items-center ${
            messageType === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {messageType === "success" ? (
            <Check className="w-4 h-4 mr-2" />
          ) : (
            <AlertTriangle className="w-4 h-4 mr-2" />
          )}
          {message}
        </div>
      )}

      {!showChangePassword ? (
        <div className="flex items-center justify-between py-3">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Password</h3>
            <p className="text-sm text-gray-500">
              Change your account password
            </p>
          </div>
          <button
            onClick={() => setShowChangePassword(true)}
            className="btn-brand-primary btn-brand-sm inline-flex items-center"
          >
            <Edit className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>Change Password</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="current-password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Current Password
            </label>
            <div className="relative">
              <input
                id="current-password"
                name="currentPassword"
                type={showPasswords.current ? "text" : "password"}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => togglePasswordVisibility("current")}
              >
                {showPasswords.current ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="new-password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              New Password
            </label>
            <div className="relative">
              <input
                id="new-password"
                name="newPassword"
                type={showPasswords.new ? "text" : "password"}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => togglePasswordVisibility("new")}
              >
                {showPasswords.new ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <label
              htmlFor="confirm-password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirm-password"
                name="confirmPassword"
                type={showPasswords.confirm ? "text" : "password"}
                required
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => togglePasswordVisibility("confirm")}
              >
                {showPasswords.confirm ? (
                  <EyeOff className="w-4 h-4 text-gray-400" />
                ) : (
                  <Eye className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="submit"
              disabled={loading}
              className="btn-brand-primary disabled:opacity-50 inline-flex items-center justify-center"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin flex-shrink-0" />
                  <span>Changing...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>Change Password</span>
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowChangePassword(false);
                setPasswordData({
                  currentPassword: "",
                  newPassword: "",
                  confirmPassword: "",
                });
                setMessage("");
              }}
              className="btn-brand-secondary-outline inline-flex items-center justify-center"
            >
              <span>Cancel</span>
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Tab Components
const IntegrationsTab = ({
  integrations,
  googleAccounts,
  connecting,
  showGoogleDetails,
  setShowGoogleDetails,
  handleConnect,
  handleDisconnect,
  handleDisconnectAccount,
  handleRefresh,
  handleTestConnection,
  getStatusIcon,
  getStatusText,
  getStatusColor,
  groupedIntegrations,
  categories,
}) => (
  <>
    {/* Quick Stats */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
      <div className="bg-white shadow rounded-lg p-3 lg:p-4">
        <div className="flex items-center">
          <Check className="w-6 h-6 lg:w-8 lg:h-8 text-brand-secondary flex-shrink-0" />
          <div className="ml-2 lg:ml-3 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">
              Connected
            </p>
            <p className="text-lg lg:text-2xl font-semibold text-gray-900">
              {integrations.filter((i) => i.status === "connected").length}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-3 lg:p-4">
        <div className="flex items-center">
          <AlertTriangle className="w-6 h-6 lg:w-8 lg:h-8 text-brand-error flex-shrink-0" />
          <div className="ml-2 lg:ml-3 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">
              Issues
            </p>
            <p className="text-lg lg:text-2xl font-semibold text-gray-900">
              {integrations.filter((i) => i.status === "error").length}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-3 lg:p-4">
        <div className="flex items-center">
          <X className="w-6 h-6 lg:w-8 lg:h-8 text-gray-400 flex-shrink-0" />
          <div className="ml-2 lg:ml-3 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">
              Available
            </p>
            <p className="text-lg lg:text-2xl font-semibold text-gray-900">
              {
                integrations.filter(
                  (i) => i.status === "not_connected" && !i.comingSoon
                ).length
              }
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-3 lg:p-4">
        <div className="flex items-center">
          <Shield className="w-6 h-6 lg:w-8 lg:h-8 text-brand-primary flex-shrink-0" />
          <div className="ml-2 lg:ml-3 min-w-0">
            <p className="text-xs lg:text-sm font-medium text-gray-500 truncate">
              Coming Soon
            </p>
            <p className="text-lg lg:text-2xl font-semibold text-gray-900">
              {integrations.filter((i) => i.comingSoon).length}
            </p>
          </div>
        </div>
      </div>
    </div>

    {/* Google Calendar Integration - Consolidated */}
    <div className="bg-white shadow rounded-lg p-4 lg:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
        <h2 className="text-lg lg:text-xl font-semibold text-gray-900 flex items-center">
          <Calendar className="w-5 h-5 lg:w-6 lg:h-6 mr-2 text-brand-primary flex-shrink-0" />
          <span className="truncate">Google Calendar Integration</span>
        </h2>
        <button
          onClick={() => setShowGoogleDetails(!showGoogleDetails)}
          className="text-sm text-brand-primary hover:text-brand-primary-dark self-start sm:self-auto"
        >
          {showGoogleDetails ? "Hide Details" : "Show Details"}
        </button>
      </div>

      {!googleAccounts.length ? (
        <div className="text-center py-8">
          <Calendar className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">
            No Google Accounts Connected
          </h3>
          <p className="text-gray-600 mb-4">
            Connect your Google account to sync calendar events
          </p>
          <button
            onClick={() => handleConnect("google-calendar")}
            disabled={connecting}
            className="btn-brand-primary"
          >
            {connecting ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Connect Google Account
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {googleAccounts.map((account, index) => (
              <div
                key={account._id}
                className="border border-gray-200 rounded-lg p-3 lg:p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    {account.profilePicture && (
                      <img
                        src={account.profilePicture}
                        alt={account.displayName}
                        className="w-8 h-8 lg:w-10 lg:h-10 rounded-full flex-shrink-0"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-900 text-sm lg:text-base truncate">
                        {account.displayName || account.email}
                      </h4>
                      <p className="text-xs lg:text-sm text-gray-600 truncate">
                        {account.email}
                      </p>
                      {account.lastSync && (
                        <p className="text-xs text-gray-500 mt-1 hidden sm:block">
                          Last synced:{" "}
                          {new Date(account.lastSync).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-1 lg:space-x-2 ml-2">
                    <button
                      onClick={() => handleTestConnection(account._id)}
                      className="p-1.5 lg:p-2 text-brand-secondary hover:bg-brand-neutral-100 rounded"
                      title="Test connection"
                    >
                      <Activity className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </button>
                    <button
                      onClick={() => handleRefresh("google-calendar")}
                      className="p-1.5 lg:p-2 text-brand-primary hover:bg-brand-neutral-100 rounded"
                      title="Refresh connection"
                    >
                      <RefreshCw className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </button>
                    <button
                      onClick={() => handleDisconnectAccount(account._id)}
                      className="p-1.5 lg:p-2 text-brand-error hover:bg-red-50 rounded"
                      title="Remove account"
                    >
                      <Trash2 className="w-3.5 h-3.5 lg:w-4 lg:h-4" />
                    </button>
                  </div>
                </div>

                {showGoogleDetails && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Account #:</span>
                        <span className="ml-2 font-medium">{index + 1}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className="ml-2 font-medium text-green-600">
                          Active
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Permissions:</span>
                        <span className="ml-2">Calendar, Events</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Added:</span>
                        <span className="ml-2">
                          {new Date(account.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <button
                onClick={() => handleConnect("google-calendar")}
                className="inline-flex items-center text-sm text-brand-primary hover:text-brand-primary-dark"
              >
                <UserPlus className="w-4 h-4 mr-1 flex-shrink-0" />
                <span>Add Another Google Account</span>
              </button>

              <div className="text-xs text-gray-500">
                <span className="font-medium">Tip:</span> Calendar events can
                now be created automatically
              </div>
            </div>
          </div>
        </>
      )}
    </div>

    {/* Other Google Services */}
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
        <Database className="w-6 h-6 mr-2 text-brand-primary" />
        Other Google Services
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {integrations
          .filter((i) => i.id !== "google-calendar")
          .map((integration) => {
            const IconComponent = integration.icon;
            return (
              <div
                key={integration.id}
                className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0">
                      <IconComponent className="w-10 h-10 text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-medium text-gray-900">
                          {integration.name}
                        </h3>
                        {integration.comingSoon && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Coming Soon
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        {integration.description}
                      </p>
                      {integration.features && (
                        <div className="flex flex-wrap gap-1">
                          {integration.features.map((feature, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="ml-4">
                    <button
                      disabled
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-400 rounded cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  </>
);

const AccountTab = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
      <UserPlus className="w-6 h-6 mr-2 text-brand-primary" />
      Account Information
    </h2>
    <ProfileSettings />
  </div>
);

const SecurityTab = () => (
  <div id="security-settings-tab" className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
      <Shield className="w-6 h-6 mr-2 text-brand-primary" />
      Security Settings
    </h2>
    <PasswordSettings />
    <AiAccessKeysPanel />
  </div>
);

const OrganizationTab = () => {
  const { user: currentUser } = useAuth();
  const [members, setMembers] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showOrgEditModal, setShowOrgEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);

  // Invite form state
  const [inviteForm, setInviteForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "coach"
  });
  const [inviting, setInviting] = useState(false);

  // Organization edit form state
  const [orgEditForm, setOrgEditForm] = useState({
    name: "",
    description: ""
  });
  const [savingOrg, setSavingOrg] = useState(false);
  const [powerUpZoomLink, setPowerUpZoomLink] = useState("");
  const [savingPowerUpLink, setSavingPowerUpLink] = useState(false);

  // Check if current user is owner or admin
  const isCurrentUserOwner = currentUser?.role === 'owner' || members.find(m => m.id === currentUser?.id)?.role === 'owner';
  const isCurrentUserAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner' ||
    members.find(m => m.id === currentUser?.id)?.role === 'admin' ||
    members.find(m => m.id === currentUser?.id)?.role === 'owner';
  const currentRoles = [
    currentUser?.role,
    ...(Array.isArray(currentUser?.roles) ? currentUser.roles : [])
  ]
    .filter(Boolean)
    .map((role) => role.toLowerCase());
  const canManagePowerUp = currentRoles.some((role) =>
    ['owner', 'admin', 'coach'].includes(role)
  );

  // Fetch members and organization on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch both organization details and members
      const [detailsData, membersData] = await Promise.all([
        organizationService.getDetails(),
        organizationService.getMembers()
      ]);
      setOrganization({
        ...detailsData.organization,
        ...membersData.organization
      });
      setPowerUpZoomLink(
        detailsData.organization?.settings?.powerUpZoomLink || ""
      );
      setMembers(membersData.members || []);
      setError("");
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const data = await organizationService.getMembers();
      setOrganization(prev => ({ ...prev, ...data.organization }));
      setMembers(data.members || []);
    } catch (err) {
      console.error("Error fetching members:", err);
      setError("Failed to load organization members");
    }
  };

  const handleEditOrganization = () => {
    setOrgEditForm({
      name: organization?.name || "",
      description: organization?.description || ""
    });
    setShowOrgEditModal(true);
  };

  const handleSavePowerUpLink = async (event) => {
    event.preventDefault();
    setSavingPowerUpLink(true);
    setError("");
    try {
      const result = await organizationService.updatePowerUpSettings(
        powerUpZoomLink
      );
      setPowerUpZoomLink(result.powerUpZoomLink || "");
      setOrganization((current) => ({
        ...current,
        settings: {
          ...(current?.settings || {}),
          powerUpZoomLink: result.powerUpZoomLink || ""
        }
      }));
      setSuccess("Power Up Zoom link updated!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update Power Up Zoom link");
    } finally {
      setSavingPowerUpLink(false);
    }
  };

  const handleSaveOrganization = async (e) => {
    e.preventDefault();
    setSavingOrg(true);
    setError("");

    try {
      const result = await organizationService.updateDetails(orgEditForm);
      setOrganization(prev => ({ ...prev, ...result.organization }));
      setSuccess("Organization updated successfully!");
      setShowOrgEditModal(false);
    } catch (err) {
      const message = err.response?.data?.message || "Failed to update organization";
      setError(message);
    } finally {
      setSavingOrg(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError("");

    try {
      await organizationService.inviteMember(inviteForm);
      setSuccess("Invitation sent successfully!");
      setShowInviteModal(false);
      setInviteForm({ firstName: "", lastName: "", email: "", role: "coach" });
      fetchMembers();
    } catch (err) {
      const message = err.response?.data?.message || "Failed to send invitation";
      setError(message);
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (userId) => {
    try {
      await organizationService.resendInvitation(userId);
      setSuccess("Invitation resent successfully!");
      setActionMenuOpen(null);
      fetchMembers();
    } catch (err) {
      setError("Failed to resend invitation");
    }
  };

  const handleRemoveMember = async (userId) => {
    const member = members.find(m => m.id === userId);
    if (member?.role === 'owner') {
      setError("Cannot remove the organization owner. Transfer ownership first.");
      setActionMenuOpen(null);
      return;
    }
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    try {
      await organizationService.removeMember(userId);
      setSuccess("Member removed successfully");
      setActionMenuOpen(null);
      fetchMembers();
    } catch (err) {
      setError("Failed to remove member");
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    try {
      await organizationService.updateMember(selectedMember.id, {
        firstName: selectedMember.firstName,
        lastName: selectedMember.lastName,
        role: selectedMember.role
      });
      setSuccess("Member updated successfully!");
      setShowEditModal(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err) {
      setError("Failed to update member");
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedMember) return;

    const confirmText = `Are you sure you want to transfer ownership to ${selectedMember.fullName}? You will become a coach and they will become the owner.`;
    if (!window.confirm(confirmText)) return;

    // Get the current user's ID - handle both id and userId (from JWT after refresh)
    const currentUserId = currentUser.id || currentUser.userId || currentUser._id;

    if (!currentUserId) {
      setError("Unable to identify current user. Please refresh the page and try again.");
      return;
    }

    try {
      // Update new owner
      await organizationService.updateMember(selectedMember.id, { role: 'owner' });
      // Update current owner to coach
      await organizationService.updateMember(currentUserId, { role: 'coach' });

      setSuccess(`Ownership transferred to ${selectedMember.fullName}`);
      setShowTransferModal(false);
      setSelectedMember(null);
      fetchMembers();
    } catch (err) {
      setError("Failed to transfer ownership");
    }
  };

  // Filter members
  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm ||
      member.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || member.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const getStatusBadge = (member) => {
    if (member.invitationStatus === "pending") {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      );
    }
    if (!member.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          <XCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        Active
      </span>
    );
  };

  const getRoleBadge = (role, isOwner = false) => {
    const roleColors = {
      owner: "bg-purple-100 text-purple-800 border border-purple-300",
      admin: "bg-blue-100 text-blue-800",
      coach: "bg-indigo-100 text-indigo-800",
      va: "bg-teal-100 text-teal-800",
      viewer: "bg-gray-100 text-gray-800"
    };
    const roleLabels = {
      owner: "Owner",
      admin: "Admin",
      coach: "Coach",
      va: "VA",
      viewer: "Viewer"
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleColors[role] || "bg-gray-100 text-gray-800"}`}>
        {role === 'owner' && <span className="mr-1">👑</span>}
        {roleLabels[role] || role}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
          <span className="ml-3 text-gray-600">Loading organization...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="organization-tab" className="space-y-6">
      {/* Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
          <Check className="w-5 h-5 mr-2" />
          {success}
          <button onClick={() => setSuccess("")} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {error}
          <button onClick={() => setError("")} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Details */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <Users className="w-6 h-6 mr-2 text-blue-600" />
                {organization?.name || "Organization"}
              </h2>
              {organization?.subscription?.plan && (
                <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  organization.subscription.plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                  organization.subscription.plan === 'team' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {organization.subscription.plan.charAt(0).toUpperCase() + organization.subscription.plan.slice(1)} Plan
                </span>
              )}
            </div>
            {organization?.description && (
              <p className="text-gray-600 mt-2">{organization.description}</p>
            )}
            <p className="text-sm text-gray-500 mt-2">
              {members.length} {members.length === 1 ? "member" : "members"}
            </p>
            <p id="organization-power-up-link-status" className="mt-2 text-sm text-gray-500">
              Power Up Zoom link: {organization?.settings?.powerUpZoomLink ? "Configured" : "Not configured"}
            </p>
          </div>
          {isCurrentUserAdmin && (
            <button
              onClick={handleEditOrganization}
              className="mt-4 sm:mt-0 inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </button>
          )}
        </div>
      </div>

      {canManagePowerUp && (
        <form
          id="power-up-zoom-settings"
          onSubmit={handleSavePowerUpLink}
          className="bg-white shadow rounded-lg p-6"
        >
          <h3 className="text-lg font-medium text-gray-900">Daily Power Up Zoom link</h3>
          <p className="mt-1 text-sm text-gray-500">
            This is the default meeting opened from the stable /power-up agent link. A link on a specific event overrides it.
          </p>
          <label htmlFor="power-up-default-zoom-link" className="mt-4 block text-sm font-medium text-gray-700">
            Zoom link
          </label>
          <input
            id="power-up-default-zoom-link"
            type="url"
            inputMode="url"
            value={powerUpZoomLink}
            onChange={(event) => setPowerUpZoomLink(event.target.value)}
            placeholder="https://zoom.us/j/..."
            className="mt-2 w-full rounded-md border border-gray-300 px-3 py-3 focus:border-blue-500 focus:ring-blue-500"
          />
          <button
            id="save-power-up-default-zoom-link"
            type="submit"
            disabled={savingPowerUpLink}
            className="mt-4 inline-flex min-h-11 items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {savingPowerUpLink ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Saving...
              </>
            ) : (
              "Save Power Up link"
            )}
          </button>
        </form>
      )}

      {/* Team Members Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage your organization's team members
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Invite Member
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
          >
            <option value="">All Roles</option>
            <option value="owner">Owner</option>
            <option value="admin">Admin</option>
            <option value="coach">Coach</option>
            <option value="va">VA</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMembers.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    {searchTerm || roleFilter ? "No members match your filters" : "No members yet. Invite your first team member!"}
                  </td>
                </tr>
              ) : (
                filteredMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-brand-primary rounded-full flex items-center justify-center text-white font-medium">
                          {member.firstName?.[0]}{member.lastName?.[0]}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {member.fullName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {member.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(member.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(member)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {member.lastLogin
                        ? new Date(member.lastLogin).toLocaleDateString()
                        : member.invitationStatus === "pending"
                        ? "Never"
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative">
                        <button
                          onClick={() => setActionMenuOpen(actionMenuOpen === member.id ? null : member.id)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <MoreVertical className="w-5 h-5" />
                        </button>
                        {actionMenuOpen === member.id && (
                          <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                            <div className="py-1">
                              <button
                                onClick={() => {
                                  setSelectedMember(member);
                                  setShowEditModal(true);
                                  setActionMenuOpen(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </button>
                              {member.invitationStatus === "pending" && (
                                <button
                                  onClick={() => handleResendInvitation(member.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                >
                                  <Mail className="w-4 h-4 mr-2" />
                                  Resend Invite
                                </button>
                              )}
                              {/* Transfer ownership - only show to owner, for active non-owner members */}
                              {isCurrentUserOwner && member.role !== 'owner' && member.isActive && member.invitationStatus !== 'pending' && (
                                <button
                                  onClick={() => {
                                    setSelectedMember(member);
                                    setShowTransferModal(true);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center"
                                >
                                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                                  Transfer Ownership
                                </button>
                              )}
                              {/* Don't show remove for owners */}
                              {member.role !== 'owner' && (
                                <button
                                  onClick={() => handleRemoveMember(member.id)}
                                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Team Member</h3>
              <form onSubmit={handleInvite}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={inviteForm.firstName}
                        onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={inviteForm.lastName}
                        onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteForm.role}
                      onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                    >
                      <option value="coach">Coach</option>
                      <option value="va">VA (Virtual Assistant)</option>
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={inviting}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {inviting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Member</h3>
              <form onSubmit={handleEditMember}>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={selectedMember.firstName}
                        onChange={(e) => setSelectedMember({ ...selectedMember, firstName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={selectedMember.lastName}
                        onChange={(e) => setSelectedMember({ ...selectedMember, lastName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      disabled
                      value={selectedMember.email}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={selectedMember.role}
                      onChange={(e) => setSelectedMember({ ...selectedMember, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-brand-primary focus:border-brand-primary"
                    >
                      <option value="coach">Coach</option>
                      <option value="va">VA (Virtual Assistant)</option>
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedMember(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Ownership Modal */}
      {showTransferModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Crown className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Transfer Ownership</h3>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This action will transfer full ownership of the organization to{" "}
                  <strong>{selectedMember.fullName}</strong>. You will become a Coach and lose administrative privileges.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-600 mb-2">New Owner:</p>
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10 bg-purple-600 rounded-full flex items-center justify-center text-white font-medium">
                    {selectedMember.firstName?.[0]}{selectedMember.lastName?.[0]}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">{selectedMember.fullName}</p>
                    <p className="text-xs text-gray-500">{selectedMember.email}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowTransferModal(false);
                    setSelectedMember(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransferOwnership}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                >
                  <ArrowRightLeft className="w-4 h-4 mr-2" />
                  Transfer Ownership
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Edit Modal */}
      {showOrgEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Organization</h3>
              <form onSubmit={handleSaveOrganization}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      required
                      value={orgEditForm.name}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={orgEditForm.description}
                      onChange={(e) => setOrgEditForm({ ...orgEditForm, description: e.target.value })}
                      placeholder="Brief description of your organization..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Optional</p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowOrgEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingOrg}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {savingOrg ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ComingSoonTab = ({ tabName }) => (
  <div className="bg-white shadow rounded-lg p-6">
    <div className="text-center py-12">
      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
        <SettingsIcon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{tabName}</h3>
      <p className="text-gray-600 mb-4">
        This feature is coming soon! We're working hard to bring you enhanced
        functionality.
      </p>
      <div className="inline-flex items-center px-3 py-2 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
        <RefreshCw className="w-4 h-4 mr-2" />
        In Development
      </div>
    </div>
  </div>
);

// Data Import Tab
const DataImportTab = () => {
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [importComplete, setImportComplete] = useState(false);

  const handleImportComplete = () => {
    setShowImportWizard(false);
    setImportComplete(true);
    // Reset after showing success message
    setTimeout(() => setImportComplete(false), 5000);
  };

  return (
    <div id="data-import-tab" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Data Import</h2>
        <p className="text-sm text-gray-500 mt-1">
          Bulk import agents and other data into your organization
        </p>
      </div>

      {importComplete && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
          <span className="text-green-700">Import completed successfully!</span>
        </div>
      )}

      {showImportWizard ? (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <AgentImportWizard
            onComplete={handleImportComplete}
            onCancel={() => setShowImportWizard(false)}
            isOnboarding={false}
          />
        </div>
      ) : (
        <div className="grid gap-4">
          {/* Agent Import Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 hover:border-brand-primary transition-colors">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-lg flex items-center justify-center mr-4">
                  <Users className="w-5 h-5 text-brand-primary" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Import Agents</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload a CSV file or paste data from a spreadsheet to bulk add agents
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowImportWizard(true)}
                className="btn-brand-primary btn-brand-sm"
              >
                Import
              </button>
            </div>
          </div>

          {/* Transaction Import Card (Coming Soon) */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 opacity-75">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                  <DollarSign className="w-5 h-5 text-gray-500" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-700">Import Transactions</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Bulk import transaction history for your agents
                  </p>
                </div>
              </div>
              <span className="px-3 py-1 bg-gray-200 text-gray-600 text-sm rounded-full">
                Coming Soon
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [showGoogleDetails, setShowGoogleDetails] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("account");
  const location = useLocation();

  const tabs = [
    { id: "account", label: "Account", icon: UserPlus },
    { id: "security", label: "Security", icon: Shield },
    { id: "integrations", label: "Integrations", icon: Calendar, comingSoon: true },
    {
      id: "organization",
      label: "Organization",
      icon: Users,
    },
    { id: "data-import", label: "Data Import", icon: Upload },
  ];

  // Google integrations only - future integrations will be added here
  const availableIntegrations = [
    {
      id: "google-calendar",
      name: "Google Calendar",
      description:
        "Sync your calendar for better time management and scheduling",
      icon: Calendar,
      category: "Google Services",
      status: "connected",
      features: ["Calendar sync", "Event creation", "Reminder automation"],
      isPopular: true,
      lastSync: "2024-01-15T10:30:00Z",
    },
    {
      id: "gmail",
      name: "Gmail",
      description:
        "Connect your Gmail account for email automation and tracking (Coming Soon)",
      icon: Mail,
      category: "Google Services",
      status: "not_connected",
      features: ["Email automation", "Contact sync", "Email templates"],
      comingSoon: true,
    },
    {
      id: "google-drive",
      name: "Google Drive",
      description:
        "Access and manage files from your Google Drive (Coming Soon)",
      icon: Database,
      category: "Google Services",
      status: "not_connected",
      features: ["File access", "Document management", "Resource sharing"],
      comingSoon: true,
    },
  ];

  useEffect(() => {
    loadData();

    // Check for URL parameters
    const params = new URLSearchParams(location.search);

    // Handle tab deep linking (e.g., /settings?tab=billing)
    const tabParam = params.get("tab");
    if (tabParam) {
      const validTab = tabs.find(t => t.id === tabParam && !t.comingSoon);
      if (validTab) {
        setActiveTab(tabParam);
      }
    }

    // Check for OAuth callback parameters
    if (params.get("success") === "google_connected") {
      setSuccessMessage("Google account connected successfully!");
      setShowGoogleDetails(true);
      loadGoogleAccounts();
      // Clear URL params
      window.history.replaceState({}, document.title, "/settings");
    } else if (params.get("error")) {
      const error = params.get("error");
      if (error === "no_code") {
        setErrorMessage("Authorization was cancelled. Please try again.");
      } else {
        setErrorMessage("Failed to connect Google account. Please try again.");
      }
      window.history.replaceState({}, document.title, "/settings");
    }
  }, [location]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load user settings
      const userSettings = await settingsService.getSettings();
      setSettings(userSettings);

      // Load Google accounts
      await loadGoogleAccounts();

      // Set integrations with updated status
      const updatedIntegrations = availableIntegrations.map((integration) => {
        if (integration.id === "google-calendar") {
          return {
            ...integration,
            status: googleAccounts.length > 0 ? "connected" : "not_connected",
            connectedAccounts: googleAccounts.length,
          };
        }
        return integration;
      });

      setIntegrations(updatedIntegrations);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleAccounts = async () => {
    try {
      const accounts = await googleService.getAccounts();
      setGoogleAccounts(accounts);

      // Update integration status
      setIntegrations((prev) =>
        prev.map((integration) => {
          if (integration.id === "google-calendar") {
            return {
              ...integration,
              status: accounts.length > 0 ? "connected" : "not_connected",
              connectedAccounts: accounts.length,
            };
          }
          return integration;
        })
      );
    } catch (error) {
      console.error("Error loading Google accounts:", error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <Check className="w-5 h-5 text-green-500" />;
      case "error":
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <X className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (integration) => {
    if (integration.comingSoon) return "Coming Soon";

    switch (integration.status) {
      case "connected":
        return integration.lastSync
          ? `Connected • Last sync: ${new Date(
              integration.lastSync
            ).toLocaleString()}`
          : "Connected";
      case "error":
        return integration.error || "Connection Error";
      default:
        return "Not Connected";
    }
  };

  const getStatusColor = (integration) => {
    if (integration.comingSoon) return "text-blue-600";

    switch (integration.status) {
      case "connected":
        return "text-green-600";
      case "error":
        return "text-red-600";
      default:
        return "text-gray-500";
    }
  };

  const handleConnect = async (integrationId) => {
    if (integrationId === "google-calendar") {
      try {
        setConnecting(true);
        const authUrl = await googleService.getAuthUrl();
        window.location.href = authUrl;
      } catch (error) {
        console.error("Error connecting to Google:", error);
        alert("Failed to connect to Google. Please try again.");
      } finally {
        setConnecting(false);
      }
    } else {
      console.log("Connecting to:", integrationId);
      // TODO: Implement other integrations
    }
  };

  const handleDisconnect = async (integrationId) => {
    if (integrationId === "google-calendar" && googleAccounts.length > 0) {
      if (
        window.confirm(
          "Are you sure you want to disconnect all Google accounts?"
        )
      ) {
        try {
          // Disconnect all accounts
          for (const account of googleAccounts) {
            await googleService.disconnectAccount(account._id);
          }
          await loadGoogleAccounts();
          alert("Google accounts disconnected successfully");
        } catch (error) {
          console.error("Error disconnecting Google accounts:", error);
          alert("Failed to disconnect Google accounts");
        }
      }
    } else {
      console.log("Disconnecting from:", integrationId);
      // TODO: Implement other integrations
    }
  };

  const handleRefresh = async (integrationId) => {
    if (integrationId === "google-calendar") {
      await loadGoogleAccounts();
    } else {
      console.log("Refreshing:", integrationId);
      // TODO: Implement refresh for other integrations
    }
  };

  const handleDisconnectAccount = async (accountId) => {
    if (
      window.confirm("Are you sure you want to disconnect this Google account?")
    ) {
      try {
        await googleService.disconnectAccount(accountId);
        await loadGoogleAccounts();
        setSuccessMessage("Google account disconnected successfully");
      } catch (error) {
        console.error("Error disconnecting account:", error);
        setErrorMessage("Failed to disconnect account. Please try again.");
      }
    }
  };

  const handleTestConnection = async (accountId) => {
    try {
      // Test by fetching a small number of events
      await googleService.getCalendarEvents(accountId, { maxResults: 1 });
      setSuccessMessage(
        "Connection test successful! Your calendar is working properly."
      );
    } catch (error) {
      console.error("Connection test failed:", error);
      setErrorMessage(
        "Connection test failed. You may need to reconnect your account."
      );
    }
  };

  const groupedIntegrations = integrations.reduce((groups, integration) => {
    const category = integration.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(integration);
    return groups;
  }, {});

  const categories = Object.keys(groupedIntegrations).sort();

  if (loading) {
    return (
      <div
        id="settings-loading"
        className="flex items-center justify-center py-12"
      >
        <RefreshCw className="w-8 h-8 animate-spin text-brand-primary" />
        <span className="ml-3 text-lg text-gray-600">Loading settings...</span>
      </div>
    );
  }

  return (
    <div id="settings-page" className="space-y-6">
      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-brand-secondary-50 border border-brand-secondary-light text-brand-secondary-dark px-4 py-3 rounded-lg flex items-center">
          <Check className="w-5 h-5 mr-2" />
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {errorMessage}
        </div>
      )}

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <SettingsIcon className="w-8 h-8 mr-3 text-brand-primary" />
              Settings
            </h1>
            <p className="text-gray-600 mt-2">
              {/* Manage your Google integrations and account settings */}
            </p>
          </div>
          <div className="mt-4 lg:mt-0 text-sm text-gray-500">
            {/* {integrations.filter(i => i.status === 'connected').length} of {integrations.length} integrations active */}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-4 lg:space-x-8 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => !tab.comingSoon && setActiveTab(tab.id)}
                  disabled={tab.comingSoon}
                  className={`
                    flex items-center whitespace-nowrap py-3 px-2 lg:px-1 border-b-2 font-medium text-sm transition-colors min-w-0 flex-shrink-0
                    ${
                      isActive
                        ? "border-brand-primary text-brand-primary"
                        : tab.comingSoon
                        ? "border-transparent text-gray-400 cursor-not-allowed"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                >
                  <Icon className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden text-xs">
                    {tab.label.split(" ")[0]}
                  </span>
                  {tab.comingSoon && (
                    <span className="ml-2 hidden lg:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                      Soon
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "integrations" && (
          <IntegrationsTab
            integrations={integrations}
            googleAccounts={googleAccounts}
            connecting={connecting}
            showGoogleDetails={showGoogleDetails}
            setShowGoogleDetails={setShowGoogleDetails}
            handleConnect={handleConnect}
            handleDisconnect={handleDisconnect}
            handleDisconnectAccount={handleDisconnectAccount}
            handleRefresh={handleRefresh}
            handleTestConnection={handleTestConnection}
            getStatusIcon={getStatusIcon}
            getStatusText={getStatusText}
            getStatusColor={getStatusColor}
            groupedIntegrations={groupedIntegrations}
            categories={categories}
          />
        )}

        {activeTab === "account" && <AccountTab />}

        {activeTab === "security" && <SecurityTab />}

        {activeTab === "organization" && <OrganizationTab />}

        {activeTab === "data-import" && <DataImportTab />}

      </div>
    </div>
  );
};

export default Settings;
